import { MTD_CHECK_FROM_DAY,
  bigChange,
  budgetBand,
  dayParts,
  expectedBy,
  monthBudget,
  settingFor,
  type BudgetBand,
  type BudgetMap,
  type KpiSettingsMap,
  type KpiType,
  type PastValue,
} from './budgets';
import { budgetQuestionIds, plausibleKpi, type KpiSummary } from './kpis';
import type { TemplateDefinition } from './template';

/**
 * B8 — what the form shows under each number/percent box (owner rulings
 * 2026-10-25): the store's last value, this month's budget, and an amber
 * "Big change — double-check" (warn only, never blocks submit). Snapshotted
 * onto the local audit session when it starts (LocalAuditSession.kpiContext)
 * so it works with no signal. Pure.
 */

export interface KpiContext {
  /** Per KPI question id: the store's last 3 values, newest first (Eastern days). */
  history: Record<string, PastValue[]>;
  /** This store's budgets for the visit year (src/audit/budgets.ts BudgetMap). */
  budgets: BudgetMap;
  /** The year `budgets` is for. */
  year: number;
  /** The template's KPI settings (direction, month-to-date or level). */
  settings: KpiSettingsMap;
  /** The template family key (AuditType) budgets are keyed by. */
  templateKey: string;
  /** When it was read (ISO) — the conduct page re-reads a stale one when online. */
  fetchedAt: string;
}

/** How long a snapshot is good for before the conduct page re-reads it (when online). */
export const KPI_CONTEXT_MAX_AGE_MS = 12 * 3_600_000;

const own = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);
function put<T>(obj: Record<string, T>, key: string, value: T) {
  Object.defineProperty(obj, key, { value, enumerable: true, writable: true, configurable: true });
}

/**
 * The key a template's budgets sit under in a BudgetMap — its AuditType,
 * matched trimmed and case-insensitively (template ids are families:
 * src/data/templateAdmin.ts familyKey). null = none.
 */
export function budgetKeyFor(map: BudgetMap | null | undefined, templateKey: string): string | null {
  if (!map) return null;
  const want = templateKey.trim().toLowerCase();
  for (const k of Object.keys(map)) if (k.trim().toLowerCase() === want) return k;
  return null;
}

/** The KPI (number/percent) questions of a definition, by id. */
export function kpiQuestions(def: TemplateDefinition): Map<string, KpiType> {
  const out = new Map<string, KpiType>();
  for (const s of def.sections ?? []) {
    for (const q of s.questions ?? []) {
      if (q.type === 'number' || q.type === 'percent') out.set(q.id, q.type);
    }
  }
  return out;
}

/**
 * Build the snapshot. `audits` = the store's SUBMITTED audits (any template
 * — a KPI id is unique to its question), each with its Eastern submit day.
 */
export function buildKpiContext(input: {
  definition: TemplateDefinition;
  templateKey: string;
  settings: KpiSettingsMap | null | undefined;
  audits: readonly { day: string; kpis: KpiSummary | null }[];
  budgets: BudgetMap | null | undefined;
  year: number;
  now: Date;
}): KpiContext {
  const questions = kpiQuestions(input.definition);
  const history: Record<string, PastValue[]> = {};
  const newestFirst = [...input.audits].filter((a) => a.day !== '').sort((a, b) => b.day.localeCompare(a.day));
  for (const [id, type] of questions) {
    const values: PastValue[] = [];
    for (const a of newestFirst) {
      if (values.length >= 3) break;
      const v = a.kpis && own(a.kpis.n, id) ? a.kpis.n[id] : undefined;
      if (typeof v === 'number' && plausibleKpi(v, type)) values.push({ day: a.day, value: v });
    }
    if (values.length > 0) put(history, id, values);
  }
  const budgets: BudgetMap = {};
  const key = budgetKeyFor(input.budgets, input.templateKey);
  if (key !== null) {
    // Only this version's budget / goal questions carry a budget hint (owner ask 2026-10-26);
    // every KPI still gets its last value and the big-change check.
    const ids = budgetQuestionIds(input.definition);
    const row: Record<string, (number | null)[]> = {};
    for (const [id, months] of Object.entries(input.budgets![key]!)) if (ids.has(id)) put(row, id, months);
    put(budgets, input.templateKey, row);
  }
  return {
    history,
    budgets,
    year: input.year,
    settings: input.settings ?? { v: 1, q: {} },
    templateKey: input.templateKey,
    fetchedAt: input.now.toISOString(),
  };
}

export interface KpiHint {
  last: PastValue | null;
  /** The month's budget, and what it means by today (pro-rated for month-to-date counts). */
  budget: { month: number; expected: number; mtd: boolean } | null;
  /** The typed value vs the expected one; null with no value or no budget. */
  band: BudgetBand | null;
  big: { average: number; now: number; mtd: boolean } | null;
}

/** What to show under one KPI box on `today` (Eastern yyyy-mm-dd). */
export function kpiHint(
  ctx: KpiContext | null | undefined,
  questionId: string,
  type: KpiType,
  value: number | null,
  today: string,
): KpiHint | null {
  if (!ctx) return null;
  const setting = settingFor(ctx.settings, questionId, type);
  const history = own(ctx.history, questionId) ? ctx.history[questionId]! : [];
  const p = dayParts(today);
  let budget: KpiHint['budget'] = null;
  if (p && p.year === ctx.year) {
    const month = monthBudget(ctx.budgets, ctx.templateKey, questionId, p.month);
    const expected = expectedBy(month, setting, today);
    if (month !== null && expected !== null) budget = { month, expected, mtd: setting.kind === 'mtd' };
  }
  const typed = value !== null && Number.isFinite(value) ? value : null;
  // Month-to-date counts: no colour before the 5th (a pro-rated budget of 1–4
  // days is noise — the same rule as the big-change pace; QA 2026-10-25).
  const tooEarly = setting.kind === 'mtd' && (p === null || p.day < MTD_CHECK_FROM_DAY);
  const band = typed !== null && budget && !tooEarly ? budgetBand(typed, budget.expected, type, setting.dir) : null;
  const change = typed !== null ? bigChange(typed, today, history, type, setting) : null;
  const big = change ? { ...change, mtd: setting.kind === 'mtd' } : null;
  if (history.length === 0 && !budget) return null;
  return { last: history[0] ?? null, budget, band, big };
}

/** "45" / "87.5" — whole numbers plain, else one decimal. */
export function kpiNumber(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
